import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ImageIcon from "@mui/icons-material/Image";
import { useForm, Controller } from "react-hook-form";
import { getApiData, postFormData } from "../../../../utils/APIHelper";
import CInput from "../../../../components/CInput";
import { toast } from "react-toastify";
import IssueLogAnnotationCanvas from "./IssueLogAnnotationCanvas";
import RenoSelect from "./components/RenoSelect";
import { ISSUE_SOURCE_TYPE } from "./components/constants";

const SELECT_CUSTOM_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 60,
    height: 60,
    border: "1px solid #d1d5db",
    borderRadius: 12,
  }),
};

const getOptionValue = (opt) => {
  if (opt == null) return null;
  if (typeof opt === "object" && opt.value !== undefined && opt.value !== null) return opt.value;
  if (typeof opt === "object" && opt.id !== undefined && opt.id !== null) return opt.id;
  return String(opt);
};
const getOptionLabel = (opt) => {
  if (opt == null) return "";
  if (typeof opt === "object") return String(opt.label ?? opt.value ?? opt.id ?? "");
  return String(opt);
};

function dataURLtoFile(dataUrl, filename = "issue-image.png") {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
  const bstr = atob(arr[1]);
  const u8arr = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
  return new File([u8arr], filename, { type: mime });
}

const PRIORITY_PILLS = [
  {
    value: "Critical",
    label: "Critical",
    activeStyle: {
      border: "1px solid #ef4444",
      backgroundColor: "rgba(239,68,68,0.1)",
      color: "#b91c1c"
    },
    inactiveStyle: {
      border: "1px solid #ef4444",
      backgroundColor: "rgba(239,68,68,0.1)",
      color: "#b91c1c"
    },

  },
  {
    value: "High",
    label: "High",
    activeStyle: {
      border: "1px solid #f97316",
      backgroundColor: "rgba(249,115,22,0.1)",
      color: "#c2410c"
    },
    inactiveStyle: {
      border: "1px solid #f97316",
      backgroundColor: "rgba(249,115,22,0.1)",
      color: "#c2410c"
    }
  },
  {
    value: "Medium",
    label: "Medium",
    activeStyle: {
      border: "1px solid #f59e0b",
      backgroundColor: "rgba(245,158,11,0.1)",
      color: "#b45309"
    },
    inactiveStyle: {
      border: "1px solid #f59e0b",
      backgroundColor: "rgba(245,158,11,0.1)",
      color: "#b45309"
    }
  },
  {
    value: "Low",
    label: "Low",
    activeStyle: {
      border: "1px solid #22c55e",
      backgroundColor: "rgba(34,197,94,0.1)",
      color: "#15803d"
    },
    inactiveStyle: {
      border: "1px solid #22c55e",
      backgroundColor: "rgba(34,197,94,0.1)",
      color: "#15803d"
    }
  },

];

const DEFAULT_VALUES = {
  roomName: "",
  activity: null,
  activityType: null,
  issueType: null,
  description: "",
};

export default function AddIssueLogModal({
  isOpen,
  onClose,
  onSuccess,
  projectId,
  activityOptions = [],
  sourceId,
}) {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [activityTypesList, setActivityTypesList] = useState([]);
  const [issueCategoriesList, setIssueCategoriesList] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [step, setStep] = useState(1);
  const [rawImage, setRawImage] = useState(null);
  const [annotatedImage, setAnnotatedImage] = useState(null);
  const [annotationMeta, setAnnotationMeta] = useState(null);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    watch,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULT_VALUES });

  const activityType = watch("activityType");
  const issueType = watch("issueType");
  const roomName = watch("roomName");
  const description = watch("description");

  useEffect(() => {
    if (!isOpen) return;
    setLoadingTypes(true);
    getApiData("api/v2/issue-categories/activity-types", "GET", null, null, true)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setActivityTypesList(list);
      })
      .catch(() => setActivityTypesList([]))
      .finally(() => setLoadingTypes(false));
  }, [isOpen]);

  const activityTypeValue = activityType?.value ?? activityType ?? "";

  useEffect(() => {
    if (!isOpen || !activityTypeValue) {
      setIssueCategoriesList([]);
      setValue("issueType", null);
      return;
    }
    const encoded = encodeURIComponent(activityTypeValue);
    setLoadingCategories(true);
    getApiData(`api/v2/issue-categories/activity-type/${encoded}`, "GET", null, null, true)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setIssueCategoriesList(list);
      })
      .catch(() => setIssueCategoriesList([]))
      .finally(() => setLoadingCategories(false));
  }, [isOpen, activityTypeValue, setValue]);

  const activityTypeOptions = activityTypesList.map((s) => ({ value: s, label: s }));
  const issueTypeOptions = issueCategoriesList.map((obj) => ({
    value: obj.id,
    label: obj.issueDescription ?? obj.category ?? String(obj.id),
    data: obj,
  }));
  const selectedCategory = issueType?.data ?? issueTypeOptions.find((o) => o.value === (issueType?.value ?? issueType))?.data;
  const severityFromPriority = selectedCategory?.severity ?? "";

  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [stream]);

  const resetAll = useCallback(() => {
    reset(DEFAULT_VALUES);
    setStep(1);
    setRawImage(null);
    setAnnotatedImage(null);
    setAnnotationMeta(null);
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    setIsCameraModalOpen(false);
  }, [reset, stream]);

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const openCameraModal = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      setStream(mediaStream);
      setIsCameraModalOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play();
        }
      }, 100);
    } catch (err) {
      console.error(err);
      toast.error("Unable to access camera. Use file upload instead.");
    }
  }, []);

  const closeCameraModal = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    setIsCameraModalOpen(false);
  }, [stream]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isCameraModalOpen) closeCameraModal();
    };
    if (isCameraModalOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isCameraModalOpen, closeCameraModal]);

  const capturePhotoFromWebcam = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    closeCameraModal();
    setRawImage(dataUrl);
    setAnnotatedImage(null);
    setAnnotationMeta(null);
    setStep(2);
  }, [closeCameraModal]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result);
      setAnnotatedImage(null);
      setAnnotationMeta(null);
      setStep(2);
    };
    reader.readAsDataURL(file);
  };

  const handleAnnotationConfirm = useCallback((dataUrl, issueLog) => {
    setAnnotatedImage(dataUrl);
    setStep(1);
    const meta = issueLog ?? { coordinates: [], markup: 0 };
    setAnnotationMeta(meta);
  }, []);

  const handleRemoveImage = useCallback(() => {
    setRawImage(null);
    setAnnotatedImage(null);
    setAnnotationMeta(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const onSubmit = async (data) => {
    const category = data.issueType?.data ?? issueTypeOptions.find((o) => o.value === (data.issueType?.value ?? data.issueType))?.data;
    if (!category) {
      toast.error("Please select an issue type");
      return;
    }
    if (!annotatedImage || !annotationMeta) {
      toast.error("Please upload and annotate an image");
      return;
    }
    try {
      setSubmitting(true);
      const meta = annotationMeta ?? { coordinates: [], markup: 0 };
      const markupCount = meta.markup ?? 0;
      const issueLogBody = {
        description: (data.description || "").trim().slice(0, 1000),
        metadata: JSON.stringify({ ...meta, markup: markupCount }),
        markup: markupCount,
        project_id: Number(projectId),
        source_id: sourceId != null ? Number(sourceId) : 0,
        source_type: ISSUE_SOURCE_TYPE.DAILY_UPDATE,
        activity_id: Number(data.activity?.value ?? data.activity) || 0,
        issue_category: category,
      };
      const file = dataURLtoFile(annotatedImage, `issue-log-${Date.now()}.png`);
      const formData = new FormData();
      formData.append("image", file, file.name || "image.png");
      formData.append("issueLog", JSON.stringify(issueLogBody));

      const response = await postFormData("api/v2/issue-logs/with-image", formData, {}, true);
      if (response?.success !== false && (response?.id != null || response?.data?.id != null)) {
        toast.success("Issue logged");
        onSuccess?.();
        handleClose();
      } else {
        toast.error(response?.message || "Failed to log issue");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to log issue");
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid =
    roomName?.trim() &&
    roomName.length <= 100 &&
    (watch("activity")?.value != null || watch("activity") != null) &&
    (activityType?.value != null || activityType != null) &&
    selectedCategory &&
    !!annotatedImage &&
    !!annotationMeta &&
    description?.trim() &&
    description.length <= 1000;

  if (!isOpen) return null;

  const showAnnotationStep = step === 2 && rawImage;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth={showAnnotationStep ? "md" : "sm"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            ...(showAnnotationStep ? { height: "85vh", minHeight: 352 } : { maxHeight: "90vh" }),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            flexShrink: 0,
            marginLeft: "10px",
            ...(showAnnotationStep ? { px: 3, pt: 2, letterSpacing: 0 } : { letterSpacing: 0 }),
          }}
        >
          <span style={{ fontWeight: 500, fontSize: 18, color: "#0d0d0e", letterSpacing: 0 }}>Log Workmanship Issue</span>
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden", p: showAnnotationStep ? 0 : 3 }}>
          {showAnnotationStep ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, padding: "0 16px 16px" }}>
              <IssueLogAnnotationCanvas
                imageSrc={rawImage}
                onConfirm={handleAnnotationConfirm}
                onBack={() => setStep(1)}
              />
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="scroll-hidden"
              style={{ display: "flex", flexDirection: "column", gap: 16, overflow: "auto", padding: "0 2px" }}
            >

              <Controller
                name="roomName"
                control={control}
                rules={{ required: "Room name is required", maxLength: { value: 100, message: "Max 100 characters" } }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <CInput
                      label="Room Name *"
                      placeholder="Enter Room Name"
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!error}
                      helpertext={error?.message ?? ""}
                    />
                  </div>
                )}
              />

              <div style={{ display: "flex", flexDirection: "row", gap: 16, flexWrap: "wrap" }}>

                <Controller
                  name="activity"
                  control={control}
                  rules={{ required: "Activity is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <div style={{ flex: 1, minWidth: 120, position: "relative", width: "100%", padding: "0 4px" }}>
                      <RenoSelect
                        options={activityOptions}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Select activity"
                        isDisabled={submitting}
                        customStyles={SELECT_CUSTOM_STYLES}
                        getOptionValue={getOptionValue}
                        getOptionLabel={getOptionLabel}
                      />
                      <label style={{ position: "absolute", left: 16, top: -8, fontSize: 14, color: "#6b7280", backgroundColor: "#fff", padding: "0 4px", zIndex: 1, pointerEvents: "none" }}>
                        Activity <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      {error && <div style={{ minHeight: 18 }}><span style={{ color: "#ef4444", fontSize: 12 }}>{error.message}</span></div>}
                    </div>
                  )}
                />
                <Controller
                  name="activityType"
                  control={control}
                  rules={{ required: "Activity type is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <div style={{ flex: 1, minWidth: 120, position: "relative", width: "100%", padding: "0 4px" }}>
                      <RenoSelect
                        options={activityTypeOptions}
                        value={field.value}
                        onChange={(v) => {
                          field.onChange(v);
                          setValue("issueType", null);
                        }}
                        onBlur={field.onBlur}
                        placeholder="Select activity type"
                        isDisabled={loadingTypes || submitting}
                        customStyles={SELECT_CUSTOM_STYLES}
                        getOptionValue={getOptionValue}
                        getOptionLabel={getOptionLabel}
                      />
                      <label style={{ position: "absolute", left: 16, top: -8, fontSize: 14, color: "#6b7280", backgroundColor: "#fff", padding: "0 4px", zIndex: 1, pointerEvents: "none" }}>
                        Activity Type <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      {error && <div style={{ minHeight: 18 }}><span style={{ color: "#ef4444", fontSize: 12 }}>{error.message}</span></div>}
                    </div>
                  )}
                />
              </div>

              <Controller
                name="issueType"
                control={control}
                rules={{ required: "Issue type is required" }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative", width: "100%", marginTop: 8 }}>
                    <RenoSelect
                      options={issueTypeOptions}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Select issue type"
                      isDisabled={!activityTypeValue || loadingCategories || submitting}
                      customStyles={SELECT_CUSTOM_STYLES}
                      getOptionValue={getOptionValue}
                      getOptionLabel={getOptionLabel}
                    />
                    <label style={{ position: "absolute", left: 16, top: -8, fontSize: 14, color: "#6b7280", backgroundColor: "#fff", padding: "0 4px", zIndex: 1, pointerEvents: "none" }}>
                      Issue Type <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    {error && <div style={{ minHeight: 18 }}><span style={{ color: "#ef4444", fontSize: 12 }}>{error.message}</span></div>}
                  </div>
                )}
              />

              {selectedCategory && (
                <>
                  <CInput label="Issue Category" value={selectedCategory?.category ?? ""} disabled placeholder="—" />
                  <div style={{ width: "100%", padding: "0 8px" }}>
                    <label style={{ display: "block", fontSize: 14, color: "#6b7280", marginBottom: 8 }}>Severity</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                      {PRIORITY_PILLS.map((p) => {
                        const isActive = (severityFromPriority || "").toLowerCase() === p.value.toLowerCase();
                        const pillStyle = isActive ? p.activeStyle : p.inactiveStyle;
                        return (
                          <label key={p.value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                            <input
                              type="radio"
                              name="severity"
                              value={p.value}
                              checked={isActive}
                              readOnly
                              style={{ width: 16, height: 16, accentColor: "#4D6BF6" }}
                            />
                            <span
                              style={{
                                padding: "4px 16px",
                                borderRadius: 9999,
                                fontSize: 14,
                                fontWeight: 500,
                                ...pillStyle,
                              }}
                            >
                              {p.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              <div>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 8, color: "#0d0d0e" }}>
                  Photo Evidence *
                </label>
                {annotatedImage ? (
                  <div style={{ position: "relative", display: "inline-block", borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb", background: "#f9fafb" }}>
                    <img src={annotatedImage} alt="Annotated" style={{ maxWidth: "100%", maxHeight: 192, objectFit: "contain", display: "block" }} />
                    <Button
                      type="button"
                      size="small"
                      onClick={handleRemoveImage}
                      disabled={submitting}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        minWidth: 32,
                        backgroundColor: "#374151",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#1f2937" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </Button>
                    <div
                      style={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        background: "#16a34a",
                        color: "#fff",
                        fontSize: 12,
                        padding: "4px 8px",
                        borderRadius: 9999,
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 14 }} /> Annotated
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleFile}
                    />
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={submitting}
                      startIcon={<CloudUploadIcon />}
                      sx={{ textTransform: "none", borderColor: "#4D6BF6", color: "#4D6BF6" }}
                    >
                      Upload file
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={openCameraModal}
                      disabled={submitting}
                      startIcon={<CameraAltIcon />}
                      sx={{ textTransform: "none", borderColor: "#4D6BF6", color: "#4D6BF6" }}
                    >
                      Open camera
                    </Button>
                  </div>
                )}
              </div>

              <Controller
                name="description"
                control={control}
                rules={{ required: "Description is required", maxLength: { value: 1000, message: "Max 1000 characters" } }}
                render={({ field, fieldState: { error } }) => (
                  <CInput
                    label="Description *"
                    placeholder="Describe the issue..."
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value.slice(0, 1000))}
                    error={!!error}
                    helpertext={(error?.message ?? "") || `${(field.value?.length ?? 0)}/1000 characters`}
                    multiline
                    rows={4}
                  />
                )}
              />

              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 8 }}>
                <Button variant="outlined" onClick={handleClose} disabled={submitting} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isFormValid || submitting}
                  sx={{ backgroundColor: "#4D6BF6", textTransform: "none" }}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {isCameraModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              backdropFilter: "blur(4px)",
            }}
            onClick={closeCameraModal}
            aria-hidden="true"
          />
          <div
            style={{
              position: "relative",
              zIndex: 10,
              width: "100%",
              maxWidth: 672,
              margin: 16,
              background: "#000",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", background: "rgba(17,24,39,0.8)" }}>
              <span style={{ fontSize: 18, fontWeight: 600, color: "#fff" }}>Camera</span>
              <IconButton onClick={closeCameraModal} sx={{ color: "#fff" }}>
                <CloseIcon />
              </IconButton>
            </div>
            <div style={{ position: "relative" }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", height: 320, objectFit: "cover", display: "block" }}
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
              <div style={{ position: "absolute", bottom: 24, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 16 }}>
                <Button
                  variant="contained"
                  onClick={capturePhotoFromWebcam}
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    color: "#4D6BF6",
                    border: "4px solid #fff",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
                    "&:hover": { backgroundColor: "#f3f4f6" },
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 28 }} />
                </Button>
              </div>
            </div>
            <div style={{ padding: "16px 24px", background: "rgba(17,24,39,0.8)" }}>
              <p style={{ textAlign: "center", fontSize: 14, color: "#9ca3af", margin: 0 }}>
                Position your camera and tap the button to capture. You will then be able to annotate the image.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
